import type { CompanyTag } from "../types/company-tag";

/**
 * Who a company tag designates, reduced to the form the web profile of
 * `POST /company/preview` is looked up by (`docs/decisions/0016`).
 *
 * Held in two copies of one logic:
 * `packages/common/src/utils/company-preview-key.ts`, which the UI keys its
 * requests with, and `api/src/company/company-preview-key.ts`, which the
 * server stores profiles under. The API cannot import a runtime value from
 * `@shaarp/common` (its barrel re-exports a directory, which Node refuses to
 * resolve from the API's CommonJS build), so it keeps its own;
 * `api/src/company/company-preview-key-parity.spec.ts` runs both on one
 * corpus, so a drift fails the suite. Each copy adds only what its side
 * needs: the server's stored key, the browser's lookup key.
 *
 * Change both files together. A mismatch is the bug this pair exists to
 * prevent: the panel keys its request by one identity while the server stores
 * the profile under another, and two companies the server tells apart share
 * one entry in the browser — the panel then shows the other one's profile.
 */

const SIRET_LENGTH = 14;
const SIREN_LENGTH = 9;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Legal forms and stop words: they name no company, so "Acme SAS" and "ACME"
 * are one name. The same list `companyNamesAreConsistent` ignores when it
 * compares two names.
 */
export const COMPANY_NAME_NOISE_TOKENS: readonly string[] = [
  "sa",
  "sas",
  "sasu",
  "sarl",
  "eurl",
  "earl",
  "gaec",
  "scea",
  "sci",
  "snc",
  "scop",
  "association",
  "ets",
  "etablissements",
  "et",
  "de",
  "des",
  "du",
  "la",
  "le",
  "les",
  "aux",
  "the",
];

const NOISE = new Set(COMPANY_NAME_NOISE_TOKENS);

/**
 * What a tag says about a company once each attribute is reduced to the form
 * it is compared and stored in. An attribute that is not valid in its own
 * format is absent here, never kept "as typed": it can neither resolve a
 * company nor name a cache row.
 */
export type NormalizedCompanyTag = {
  id?: string;
  siret?: string;
  siren?: string;
  domain?: string;
  name?: string;
  city?: string;
};

/** A SIRET or SIREN: digits only, and only with exactly `length` of them. */
const digitsOf = (
  value: string | undefined,
  length: number,
): string | undefined => {
  const digits = value?.replace(/\D/g, "");
  return digits?.length === length ? digits : undefined;
};

export const normalizeSiret = (value: string | undefined): string | undefined =>
  digitsOf(value, SIRET_LENGTH);

export const normalizeSiren = (value: string | undefined): string | undefined =>
  digitsOf(value, SIREN_LENGTH);

/**
 * The canonical domain: lowercase host, no scheme, no `www.`, no path. A value
 * with no dot (`localhost`, a word) is not a company's domain.
 */
export const normalizeDomain = (
  value: string | undefined,
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  let host: string;
  try {
    const withScheme = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    host = new URL(withScheme).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return undefined;
  }
  return host.includes(".") ? host : undefined;
};

/**
 * A name or a city as a comparison key: lowercase, accents removed,
 * punctuation turned into a space, whitespace collapsed. `Société Générale,
 * Paris-La Défense` and `societe generale paris la defense` are one key.
 */
export const normalizeText = (
  value: string | undefined,
): string | undefined => {
  const text = value
    ?.normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
  return text || undefined;
};

/**
 * A company name as a key: {@link normalizeText} without its legal forms and
 * stop words, so `Acme SAS`, `ACME` and `Établissements Acme` are one name. A
 * name made only of them (`La SA`) keeps them: it is all there is.
 */
export const normalizeCompanyName = (
  value: string | undefined,
): string | undefined => {
  const text = normalizeText(value);
  const core = text
    ?.split(" ")
    .filter((token) => !NOISE.has(token))
    .join(" ");
  return core || text;
};

/** The id of a Shaarp company, only when it is a UUID. */
export const normalizeCompanyId = (
  value: string | undefined,
): string | undefined => {
  const id = value?.trim().toLowerCase();
  return id && UUID_PATTERN.test(id) ? id : undefined;
};

/**
 * The tag in comparable form. A SIRET starts with its SIREN: when the tag has
 * a valid SIRET, the SIREN is the SIRET's, whatever the tag wrote beside it —
 * a tag cannot name one establishment and another company's SIREN.
 */
export function normalizeCompanyTag(tag: CompanyTag): NormalizedCompanyTag {
  const siret = normalizeSiret(tag.siret);
  return {
    id: normalizeCompanyId(tag.id),
    siret,
    siren: siret ? siret.slice(0, SIREN_LENGTH) : normalizeSiren(tag.siren),
    domain: normalizeDomain(tag.domain),
    name: normalizeCompanyName(tag.name),
    city: normalizeText(tag.city),
  };
}

/**
 * The identity a profile is stored under when no Shaarp company backs the
 * tag: the most precise identifier — SIRET, SIREN, domain — when the tag has
 * one, bound to the name and to the city.
 *
 * Bound to the name because an identifier the tag carries is a claim nothing
 * has checked: a model that wrote the wrong SIREN, or a request that forged
 * one, would otherwise file a profile of another company under the key every
 * team asking about the real one is served. Bound to the city because the run
 * searches with it: a city that steered the searches without entering the key
 * would let one asker's city decide the row every other asker is served.
 * `null` when the tag has no name left to key on.
 */
export function companyPreviewIdentity(
  tag: NormalizedCompanyTag,
): string | null {
  const parts = identityParts(tag);
  if (!parts) return null;
  return parts.identifier ? `${parts.identifier}|${parts.name}` : parts.name;
}

/**
 * The two halves of {@link companyPreviewIdentity}: the most precise
 * identifier, when the tag has one, and the name with its city.
 */
function identityParts(
  tag: NormalizedCompanyTag,
): { identifier: string | null; name: string } | null {
  if (!tag.name) return null;
  const name = tag.city
    ? `name:${tag.name}|city:${tag.city}`
    : `name:${tag.name}`;
  if (tag.siret) return { identifier: `siret:${tag.siret}`, name };
  if (tag.siren) return { identifier: `siren:${tag.siren}`, name };
  if (tag.domain) return { identifier: `domain:${tag.domain}`, name };
  return { identifier: null, name };
}

/**
 * The key the browser files a tag's web profile under: every attribute the
 * server resolves the tag with (its id, then SIRET, SIREN, domain), the name
 * and the city.
 *
 * Never coarser than the server's key: two tags with one browser key always
 * get one profile from the server, so a panel can serve a profile it already
 * holds without asking again. `null` exactly when the server refuses the tag.
 */
export function companyPreviewLookupKey(tag: CompanyTag): string | null {
  const normalized = normalizeCompanyTag(tag);
  if (!normalized.name) return null;
  return [
    normalized.id && `id:${normalized.id}`,
    normalized.siret && `siret:${normalized.siret}`,
    normalized.siren && `siren:${normalized.siren}`,
    normalized.domain && `domain:${normalized.domain}`,
    `name:${normalized.name}`,
    normalized.city && `city:${normalized.city}`,
  ]
    .filter(Boolean)
    .join("|");
}
